"use client";

import { useClerk, useSignIn } from "@clerk/nextjs";
import { useCallback, useState } from "react";
import {
  hasClerkErrorCode,
  isClerkApiResponseError,
} from "@/features/auth/lib/clerk-errors";
import { saveSsoAttempt } from "@/features/auth/lib/sso-attempt-storage";
import type { OAuthProvider } from "@/features/auth/types/auth-flow.type";
import { useSessionNavigate } from "./use-session-navigate";

/** Clerk returns this code when no account matches the given identifier. */
const IDENTIFIER_NOT_FOUND = "form_identifier_not_found";

type FirstFactor = { strategy: string };

const hasPasswordFactor = (factors: readonly FirstFactor[]) =>
  factors.some((factor) => factor.strategy === "password");

const getOAuthProvider = (
  factors: readonly FirstFactor[],
): OAuthProvider | null => {
  if (factors.some((factor) => factor.strategy === "oauth_google")) {
    return "google";
  }
  if (factors.some((factor) => factor.strategy === "oauth_apple")) {
    return "apple";
  }
  return null;
};

type UseSignInActionsParams = {
  redirectUrl: string | null;
  /** Full URL Clerk returns to after the OAuth provider round trip. */
  ssoCallbackUrl: string;
  /** Known e-mail, carried into an OAuth attempt so the callback can use it. */
  email: string;
  onSignedIn?: () => void;
  setIsSubmitting: (value: boolean) => void;
  setGlobalError: (key: string | null) => void;
  /** The e-mail belongs to an account that signs in with a password. */
  onPasswordRequired: (email: string) => void;
  /** The e-mail belongs to an account usable only through its provider. */
  onProviderRequired: (email: string, provider: OAuthProvider) => void;
  /** No account for this e-mail yet — continue as registration. */
  onAccountMissing: (email: string) => void;
};

export const useSignInActions = ({
  redirectUrl,
  ssoCallbackUrl,
  email,
  onSignedIn,
  setIsSubmitting,
  setGlobalError,
  onPasswordRequired,
  onProviderRequired,
  onAccountMissing,
}: UseSignInActionsParams) => {
  const { signIn } = useSignIn();
  const clerk = useClerk();
  const navigate = useSessionNavigate({ redirectUrl, onSignedIn });
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const submitEmail = useCallback(
    async (nextEmail: string) => {
      setIsSubmitting(true);
      setGlobalError(null);

      try {
        const { error } = await signIn.create({ identifier: nextEmail });

        if (error) {
          if (hasClerkErrorCode(error, IDENTIFIER_NOT_FOUND)) {
            onAccountMissing(nextEmail);
            return;
          }

          setGlobalError("auth.genericError");
          return;
        }

        const factors = signIn.supportedFirstFactors;
        const linkedProvider = getOAuthProvider(factors);

        // A password beats a linked social account: the user typed an e-mail,
        // so the password prompt is the shortest path for them.
        if (hasPasswordFactor(factors) || !linkedProvider) {
          setPasswordError(null);
          onPasswordRequired(nextEmail);
          return;
        }

        onProviderRequired(nextEmail, linkedProvider);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      onAccountMissing,
      onPasswordRequired,
      onProviderRequired,
      setGlobalError,
      setIsSubmitting,
      signIn,
    ],
  );

  const submitPassword = useCallback(
    async (password: string) => {
      setIsSubmitting(true);
      setPasswordError(null);
      setGlobalError(null);

      try {
        const { error } = await signIn.password({ password });

        if (error) {
          // Every rejection the API answers with lands on the single error
          // state the design defines; a dropped request is a different story.
          if (isClerkApiResponseError(error)) {
            setPasswordError("auth.password.validators.invalidCredentials");
          } else {
            setGlobalError("auth.genericError");
          }
          return;
        }

        if (signIn.status !== "complete") {
          // Second factors and forced password resets are handled by their own
          // flows (MPL-3929); here they can only mean "we cannot finish".
          setGlobalError("auth.genericError");
          return;
        }

        await signIn.finalize({ navigate });
      } finally {
        setIsSubmitting(false);
      }
    },
    [navigate, setGlobalError, setIsSubmitting, signIn],
  );

  const signInWithProvider = useCallback(
    async (provider: OAuthProvider) => {
      setIsSubmitting(true);
      setGlobalError(null);

      saveSsoAttempt({ provider, email: email || null });

      // `signIn.sso()` silently does nothing once the e-mail step has created a
      // sign-in attempt; `authenticateWithRedirect` always starts a new one and
      // either leaves for the provider or throws.
      try {
        const attempt = clerk.client?.signIn;
        if (!attempt) {
          throw new Error("Clerk client is not loaded");
        }

        await attempt.authenticateWithRedirect({
          strategy: `oauth_${provider}`,
          redirectUrl: ssoCallbackUrl,
          redirectUrlComplete: redirectUrl ?? ssoCallbackUrl,
        });
      } catch {
        setGlobalError("auth.genericError");
        setIsSubmitting(false);
      }
    },
    [
      clerk,
      email,
      redirectUrl,
      setGlobalError,
      setIsSubmitting,
      ssoCallbackUrl,
    ],
  );

  const resetSignIn = useCallback(async () => {
    setPasswordError(null);
    await signIn.reset();
  }, [signIn]);

  /** Identifier of an interrupted sign-in, used by the OAuth callback. */
  const getIdentifier = useCallback(() => signIn.identifier, [signIn]);

  return {
    getIdentifier,
    passwordError,
    resetSignIn,
    signInWithProvider,
    submitEmail,
    submitPassword,
  };
};
